/**
 * Format booking date and time with proper timezone conversion
 * @param dateString - ISO date string from API (e.g., "2026-03-12T04:00:00.000Z")
 * @param timezone - IANA timezone string (e.g., "America/Denver")
 * @returns Formatted date string (e.g., "Wed, Mar 12, 10:00 pm")
 */
export const formatBookingDateTime = (
  dateString: string,
  timezone: string
): string => {
  try {
    const date = new Date(dateString);
    
    // Format date part: "Wed, Mar 12"
    const datePart = date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: timezone,
    });
    
    // Format time part: "10:00 pm"
    const timePart = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone,
    }).toLowerCase();
    
    return `${datePart}, ${timePart}`;
  } catch (error) {
    console.error('Error formatting booking date/time:', error);
    // Fallback without timezone
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }
};

/**
 * Format booking date only with timezone
 * @param dateString - ISO date string from API
 * @param timezone - IANA timezone string
 * @returns Formatted date string (e.g., "Wed, Mar 12")
 */
export const formatBookingDate = (
  dateString: string,
  timezone: string
): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: timezone,
    });
  } catch (error) {
    console.error('Error formatting booking date:', error);
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }
};

/**
 * Format booking time only with timezone
 * @param dateString - ISO date string from API
 * @param timezone - IANA timezone string
 * @returns Formatted time string (e.g., "10:00 pm")
 */
export const formatBookingTime = (
  dateString: string,
  timezone: string
): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone,
    }).toLowerCase();
  } catch (error) {
    console.error('Error formatting booking time:', error);
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }
};

/**
 * Get time range for booking with timezone
 * @param startTime - ISO date string for start time
 * @param endTime - ISO date string for end time
 * @param timezone - IANA timezone string
 * @returns Formatted time range (e.g., "10:00 pm - 11:00 pm")
 */
export const formatBookingTimeRange = (
  startTime: string,
  endTime: string,
  timezone: string
): string => {
  try {
    const start = formatBookingTime(startTime, timezone);
    const end = formatBookingTime(endTime, timezone);
    return `${start} - ${end}`;
  } catch (error) {
    console.error('Error formatting booking time range:', error);
    return '';
  }
};

/**
 * Get "today" as a local-midnight Date, using the location's timezone (not the
 * device's) to decide which calendar day it currently is. Downstream code can
 * keep doing local Date arithmetic (getDate/setDate/etc.) on the result — only
 * *which* day counts as "today" changes, not how days are manipulated.
 *
 * Falls back to the device's local "today" if no timezone is provided.
 */
export const getTodayInTimezone = (timezone?: string): Date => {
  if (!timezone) {
    const fallback = new Date();
    fallback.setHours(0, 0, 0, 0);
    return fallback;
  }
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const year = Number(parts.find(p => p.type === 'year')?.value);
    const month = Number(parts.find(p => p.type === 'month')?.value);
    const day = Number(parts.find(p => p.type === 'day')?.value);
    return new Date(year, month - 1, day);
  } catch (error) {
    console.error('Error computing today in timezone:', error);
    const fallback = new Date();
    fallback.setHours(0, 0, 0, 0);
    return fallback;
  }
};
